import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Alert,
  Paper,
  Grid,
  Link,
  Avatar
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { HowToReg as RegisterIcon, Business as BusinessIcon } from '@mui/icons-material';

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [organizationLocation, setOrganizationLocation] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { register, login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (!organizationName.trim()) {
      setError('Organization name is required');
      setLoading(false);
      return;
    }

    const result = await register({
      email,
      password,
      username: name, // Use name as username
      organizationName: organizationName, // Include organization name
      organizationLocation: organizationLocation // Include organization location (can be empty)
    });
    
    if (result.success) {
      // Auto-login after registration
      const loginResult = await login(email, password);
      if (loginResult.success) {
        navigate('/');
      } else {
        setError('Registration successful, but login failed');
      }
    } else {
      setError(result.error || 'Registration failed');
    }
    
    setLoading(false);
  };

  return (
    <Grid container component="main" sx={{ height: '100vh', backgroundColor: 'background.default' }}>
      <Grid 
        item 
        xs={false} 
        sm={4} 
        md={7} 
        sx={{
          backgroundImage: 'url(https://source.unsplash.com/1600x900/?office,workspace)',
          backgroundRepeat: 'no-repeat',
          backgroundColor: (t) => 
            t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900],
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={0} square>
        <Box
          sx={{
            my: 8,
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ 
            mb: 3, 
            width: 64, 
            height: 64, 
            backgroundColor: 'primary.main' 
          }}>
            <BusinessIcon sx={{ fontSize: 36, color: 'white' }} />
          </Avatar>
          
          <Typography component="h1" variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Create Account
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Join us today to manage your invoices with AI
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {typeof error === 'string' ? error : 
                Array.isArray(error) ? 
                  error.map((err, idx) => <div key={idx}>{err.msg || JSON.stringify(err)}</div>) : 
                  error.msg || error.detail || JSON.stringify(error) || 'An error occurred'}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="name"
              label="Full Name"
              name="name"
              autoComplete="name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              InputProps={{
                sx: {
                  borderRadius: 2,
                  backgroundColor: 'background.paper'
                }
              }}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="organizationName"
              label="Organization Name"
              name="organizationName"
              autoComplete="organization"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              InputProps={{
                sx: {
                  borderRadius: 2,
                  backgroundColor: 'background.paper'
                }
              }}
              helperText="This will be your organization's name for managing invoices"
            />
            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              id="organizationLocation"
              label="Organization Location (Optional)"
              name="organizationLocation"
              autoComplete="organization-location"
              value={organizationLocation}
              onChange={(e) => setOrganizationLocation(e.target.value)}
              InputProps={{
                sx: {
                  borderRadius: 2,
                  backgroundColor: 'background.paper'
                }
              }}
              helperText="Enter your organization's location (optional)"
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                sx: {
                  borderRadius: 2,
                  backgroundColor: 'background.paper'
                }
              }}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                sx: {
                  borderRadius: 2,
                  backgroundColor: 'background.paper'
                }
              }}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                sx: {
                  borderRadius: 2,
                  backgroundColor: 'background.paper'
                }
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              startIcon={<RegisterIcon />}
              sx={{ 
                mt: 3, 
                mb: 2, 
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2,
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark'
                }
              }}
            >
              {loading ? 'Creating Account...' : 'Sign Up'}
            </Button>
            
            <Grid container justifyContent="center">
              <Grid item>
                <Link 
                  variant="body2" 
                  color="primary"
                  onClick={() => navigate('/login')}
                  sx={{ cursor: 'pointer' }}
                >
                  {"Already have an account? Sign In"}
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default RegisterPage;